import { useState } from 'react';

import { Account, AccountSaveRequest, AccountType, DataType, LifecycleStatus } from '@ndvr/portfolio-api';

import { CopiableGuid } from '@/components/CopiableGuid';
import { EditableCard } from '@/components/EditableCard';
import { useEditableCardGroup } from '@/components/EditableCardGroupContext';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { formatCurrency, formatDateTime, titleCase } from 'utils';
import { requiresBirthYear } from 'views/account/account-constants';
import { useAccountGroup } from 'views/portfolio/account-group-queries';
import { useUpdateAccount } from 'views/portfolio/account-queries';
import { PersonOrgNameLink } from 'widgets';

export const GeneralCard = ({ account, editGroup }: { account: Account; editGroup?: string }) => {
    const [editableAccount, setEditableAccount] = useState<Partial<Account>>({});

    const { mutate: updateAccount, isPending } = useUpdateAccount(account.accountGUID, account.accountGroupGUID);
    const { data: accountGroup } = useAccountGroup(account.accountGroupGUID);
    const editGroupContext = useEditableCardGroup();
    const isEditing = editGroup ? editGroupContext.isEditing(editGroup, 'general') : false;

    const accountRequiresBirthYear = requiresBirthYear(account.accountType);

    const handleEdit = () => {
        setEditableAccount({
            accountName: {
                ...account.accountName,
                plainText: account.accountName?.plainText || '',
            },
            accountType: account.accountType,
            accountOwnerBirthYear: account.accountOwnerBirthYear,
            externallyManaged: account.externallyManaged,
            forecastStartingValue: account.forecastStartingValue,
        });
    };

    const handleCancel = () => {
        setEditableAccount({});
    };

    const handleSave = () => {
        const request: AccountSaveRequest = {
            accountName: editableAccount.accountName!,
            accountType: editableAccount.accountType!,
            accountOwnerBirthYear: editableAccount.accountOwnerBirthYear,
            externallyManaged: editableAccount.externallyManaged || false,
            forecastStartingValue: editableAccount.forecastStartingValue || '0',
            taxLossHarvesting: editableAccount.accountType !== AccountType.TAXABLE ? false : account.taxLossHarvesting,
            spouseBirthYear: account.spouseBirthYear,
            yearAccountOpened: account.yearAccountOpened,
            inherited: account.inherited,
            yearOfInheritance: account.yearOfInheritance,
            equityImplementation: account.equityImplementation,
        };

        updateAccount(request, {
            onSuccess: () => {
                if (editGroup) editGroupContext.setEditingCard(editGroup, null);
            },
        });
    };

    const isDraftAccount = account.lifecycleStatus === LifecycleStatus.DRAFT;
    const editableRequiresBirthYear = requiresBirthYear(editableAccount.accountType);

    const hasNameChanged = editableAccount.accountName?.plainText !== account.accountName?.plainText;
    const hasTypeChanged = editableAccount.accountType !== account.accountType;
    const hasBirthYearChanged = editableAccount.accountOwnerBirthYear !== account.accountOwnerBirthYear;
    const hasExternallyManagedChanged = editableAccount.externallyManaged !== account.externallyManaged;
    const hasForecastValueChanged = editableAccount.forecastStartingValue !== account.forecastStartingValue;
    const hasChanges =
        hasNameChanged ||
        hasTypeChanged ||
        hasBirthYearChanged ||
        hasExternallyManagedChanged ||
        hasForecastValueChanged;
    const accountNameMissing = !editableAccount.accountName?.plainText?.trim();
    const birthYearMissing = editableRequiresBirthYear && !editableAccount.accountOwnerBirthYear;

    const isValidForecastValue = (value: string | undefined) => {
        if (!value?.trim()) return false;
        const numValue = Number(value);
        return !isNaN(numValue) && numValue >= 0;
    };

    const saveDisabled =
        accountNameMissing ||
        !accountGroup?.householdGUID ||
        !hasChanges ||
        birthYearMissing ||
        (editableAccount.externallyManaged && !isValidForecastValue(editableAccount.forecastStartingValue));

    let saveTooltipMessage = saveDisabled ? 'No changes to save' : '';
    if (birthYearMissing) saveTooltipMessage = 'Account owner birth year is required';
    if (accountNameMissing) saveTooltipMessage = 'Account name is required';

    return (
        <EditableCard
            headerClassName="pt-3 pb-3"
            title="General"
            editGroup={editGroup}
            editCardId="general"
            onEdit={handleEdit}
            onCancel={handleCancel}
            onSave={handleSave}
            saveDisabled={saveDisabled}
            saving={isPending}
            saveTooltipMessage={saveTooltipMessage}
            hasChanges={hasChanges}
        >
            <div className="space-y-4">
                <div className="space-y-2">
                    <div className="text-muted-foreground text-sm font-medium">Account Name</div>
                    <div className="font-medium">
                        {isEditing ? (
                            <Input
                                value={editableAccount.accountName?.plainText || ''}
                                onChange={(e) =>
                                    setEditableAccount((prev) => ({
                                        ...prev,
                                        accountName: {
                                            plainText: e.target.value,
                                            dataType: DataType.PORTFOLIO_NAME,
                                            encryptionContextGUID: accountGroup!.householdGUID,
                                        },
                                    }))
                                }
                                disabled={!accountGroup}
                                placeholder="Enter account name"
                                className="w-full"
                            />
                        ) : (
                            account.accountName?.plainText || '-'
                        )}
                    </div>
                </div>

                <div className="space-y-2">
                    <div className="text-muted-foreground text-sm font-medium">Account Type</div>
                    <div className="font-medium">
                        {isEditing ? (
                            <Select
                                value={editableAccount.accountType}
                                onValueChange={(value) =>
                                    setEditableAccount((prev) => ({
                                        ...prev,
                                        accountType: value as AccountType,
                                    }))
                                }
                            >
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select account type" />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.values(AccountType).map((type) => (
                                        <SelectItem key={type} value={type}>
                                            {titleCase(type.replace(/_/g, ' '))}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        ) : (
                            titleCase(account.accountType.replace(/_/g, ' '))
                        )}
                    </div>
                </div>

                {(accountRequiresBirthYear || editableRequiresBirthYear) && (
                    <div className="space-y-2">
                        <div className="text-muted-foreground text-sm font-medium">Account Owner Birth Year</div>
                        <div className="font-medium">
                            {isEditing ? (
                                <Input
                                    type="number"
                                    value={editableAccount.accountOwnerBirthYear ?? ''}
                                    onChange={(e) =>
                                        setEditableAccount((prev) => ({
                                            ...prev,
                                            accountOwnerBirthYear: e.target.value
                                                ? parseInt(e.target.value)
                                                : undefined,
                                        }))
                                    }
                                    placeholder="Enter birth year (e.g., 1980)"
                                    min="1900"
                                    max={new Date().getFullYear()}
                                    className="w-full"
                                    aria-invalid={birthYearMissing}
                                    aria-describedby={birthYearMissing ? 'account-owner-birth-year-error' : undefined}
                                />
                            ) : (
                                (account.accountOwnerBirthYear ?? '-')
                            )}
                        </div>
                        {isEditing && birthYearMissing && (
                            <p id="account-owner-birth-year-error" className="text-destructive text-sm">
                                Account owner birth year is required for this account type.
                            </p>
                        )}
                    </div>
                )}

                <div className="space-y-2">
                    <div className="text-muted-foreground text-sm font-medium">Account Owner</div>
                    <div className="font-medium">
                        {account.ownerPersonOrgGUID ? (
                            <PersonOrgNameLink personOrgGUID={account.ownerPersonOrgGUID} />
                        ) : (
                            '-'
                        )}
                    </div>
                </div>

                <div className="space-y-2">
                    <div className="text-muted-foreground text-sm font-medium">Management Type</div>
                    <div className="font-medium">{titleCase(account.managementType)}</div>
                </div>

                <div className="space-y-2">
                    <div className="text-muted-foreground text-sm font-medium">Trading Disabled At</div>
                    <div className="font-medium">{formatDateTime(account.tradingSuspendedAt) || '-'}</div>
                </div>

                <div className="space-y-2">
                    <div className="text-muted-foreground text-sm font-medium">Custody Account ID</div>
                    <div className="font-medium">
                        {account.custodyAccountId ? (
                            <CopiableGuid guid={account.custodyAccountId} showFullGuid={true} />
                        ) : (
                            '-'
                        )}
                    </div>
                </div>

                <div className="space-y-2">
                    <div className="text-muted-foreground text-sm font-medium">Custody Type</div>
                    <div className="font-medium">{titleCase(account.custodyType) || '-'}</div>
                </div>

                {isDraftAccount && (
                    <div className="space-y-2">
                        <div className="text-muted-foreground text-sm font-medium">Held Away</div>
                        <div className="font-medium">
                            {isEditing ? (
                                <div className="flex items-center space-x-2">
                                    <Switch
                                        checked={editableAccount.externallyManaged || false}
                                        onCheckedChange={(checked) =>
                                            setEditableAccount((prev) => ({
                                                ...prev,
                                                externallyManaged: checked,
                                            }))
                                        }
                                    />
                                    <span>{editableAccount.externallyManaged ? 'Yes' : 'No'}</span>
                                </div>
                            ) : account.externallyManaged ? (
                                'Yes'
                            ) : (
                                'No'
                            )}
                        </div>
                    </div>
                )}

                {(isEditing ? editableAccount.externallyManaged : account.externallyManaged) && (
                    <div className="space-y-2">
                        <div className="text-muted-foreground text-sm font-medium">Held Away AUM</div>
                        <div className="font-medium">
                            {isEditing ? (
                                <Input
                                    type="number"
                                    step="1"
                                    min="0"
                                    value={editableAccount.forecastStartingValue || ''}
                                    onChange={(e) =>
                                        setEditableAccount((prev) => ({
                                            ...prev,
                                            forecastStartingValue: e.target.value,
                                        }))
                                    }
                                    placeholder="Enter starting value (e.g., 100000.00)"
                                    className="w-full"
                                />
                            ) : (
                                formatCurrency(account.forecastStartingValue)
                            )}
                        </div>
                    </div>
                )}
            </div>
        </EditableCard>
    );
};
